import React from 'react';
import { GameCartridge } from '@/types';
import { 
  UserIcon, 
  UsersIcon, 
  ArrowLeftIcon,
  PlayIcon,
  GlobeAltIcon,
  LockClosedIcon,
  SparklesIcon
} from '@/components/icons';

interface ModeSelectionScreenProps {
  selectedCartridge: GameCartridge;
  onSelectSinglePlayer: () => void;
  onSelectMultiplayer: () => void;
  onSelectGraphical?: () => void;
  onBack: () => void;
}

const ModeSelectionScreen: React.FC<ModeSelectionScreenProps> = ({
  selectedCartridge,
  onSelectSinglePlayer,
  onSelectMultiplayer,
  onSelectGraphical,
  onBack
}) => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-4 md:p-6 lg:p-8 font-sans">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <header className="flex items-center mb-8 pb-4 border-b border-slate-700">
          <button 
            onClick={onBack}
            className="mr-4 p-2 rounded-full hover:bg-slate-700 transition-colors"
            aria-label="Back to Cartridge Selection"
          >
            <ArrowLeftIcon className="w-6 h-6 text-slate-300" />
          </button>
          <div>
            <h1 className="text-3xl font-orbitron text-cyan-400 neon-text-cyan mb-2">
              Choose Your Adventure
            </h1>
            <p className="text-slate-400">
              Selected: <span className="font-semibold text-amber-300">{selectedCartridge.name}</span>
            </p>
          </div>
        </header>

        {/* Cartridge Info */}
        <div className="mb-8 p-6 bg-slate-800/60 rounded-lg border border-slate-700">
          <div className="flex flex-col md:flex-row gap-6">
            {selectedCartridge.thumbnailUrl && (
              <img 
                src={selectedCartridge.thumbnailUrl} 
                alt={selectedCartridge.name}
                className="w-full md:w-48 h-32 object-cover rounded-lg"
              />
            )}
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-slate-100 mb-2">{selectedCartridge.name}</h2>
              <p className="text-slate-300 mb-3">{selectedCartridge.description}</p>
              <div className="flex flex-wrap gap-2">
                {selectedCartridge.tags.map((tag, index) => (
                  <span 
                    key={index}
                    className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Mode Selection Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
          {/* Single Player Mode */}
          <div 
            onClick={onSelectSinglePlayer}
            className="p-8 bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl border border-slate-700 hover:border-cyan-500/50 transition-all duration-300 cursor-pointer group hover:shadow-cyan-500/20 hover:shadow-lg transform hover:scale-105"
          >
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-cyan-500/20 rounded-full flex items-center justify-center group-hover:bg-cyan-500/30 transition-colors">
                <UserIcon className="w-8 h-8 text-cyan-400" />
              </div>
              <h3 className="text-2xl font-orbitron text-slate-100 mb-3">Solo Journey</h3>
              <p className="text-slate-300 mb-4">
                Embark on a personal adventure through the digital realm. Your choices shape your unique story.
              </p>
              <div className="space-y-2 text-sm text-slate-400 mb-6">
                <div className="flex items-center justify-center gap-2">
                  <LockClosedIcon className="w-4 h-4" />
                  <span>Private Experience</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <PlayIcon className="w-4 h-4" />
                  <span>Immediate Start</span>
                </div>
              </div>
              <button className="w-full py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold rounded-lg transition-all transform group-hover:scale-105">
                Start Solo Adventure
              </button>
            </div>
          </div>

          {/* Multiplayer Mode */}
          <div 
            onClick={onSelectMultiplayer}
            className="p-8 bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl border border-slate-700 hover:border-fuchsia-500/50 transition-all duration-300 cursor-pointer group hover:shadow-fuchsia-500/20 hover:shadow-lg transform hover:scale-105"
          >
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-fuchsia-500/20 rounded-full flex items-center justify-center group-hover:bg-fuchsia-500/30 transition-colors">
                <UsersIcon className="w-8 h-8 text-fuchsia-400" />
              </div>
              <h3 className="text-2xl font-orbitron text-slate-100 mb-3">Shared Reality</h3>
              <p className="text-slate-300 mb-4">
                Join others in a collaborative narrative where every player's decisions impact the shared world.
              </p>
              <div className="space-y-2 text-sm text-slate-400 mb-6">
                <div className="flex items-center justify-center gap-2">
                  <GlobeAltIcon className="w-4 h-4" />
                  <span>Public & Private Rooms</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <UsersIcon className="w-4 h-4" />
                  <span>Up to {selectedCartridge.maxPlayers} Players</span>
                </div>
              </div>
              <button className="w-full py-3 bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:from-fuchsia-500 hover:to-purple-500 text-white font-semibold rounded-lg transition-all transform group-hover:scale-105">
                Browse Multiplayer Rooms
              </button>
            </div>
          </div>
        {onSelectGraphical && (
          <div
            onClick={onSelectGraphical}
            className="p-8 bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl border border-cyan-500/30 hover:border-cyan-400/60 transition-all duration-300 cursor-pointer group hover:shadow-cyan-400/10 hover:shadow-xl transform hover:scale-105"
          >
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-purple-500/20 rounded-full flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
                <SparklesIcon className="w-8 h-8 text-purple-300" />
              </div>
              <h3 className="text-2xl font-orbitron text-slate-100 mb-3">Visual Dreamscape (Alpha)</h3>
              <p className="text-slate-300 mb-4">
                Preview the upcoming graphical overworld. Wander Neo-Veridia with tile-based visuals while development continues.
              </p>
              <div className="space-y-2 text-sm text-slate-400 mb-6">
                <div className="flex items-center justify-center gap-2">
                  <SparklesIcon className="w-4 h-4" />
                  <span>Work-in-progress preview</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <PlayIcon className="w-4 h-4" />
                  <span>Switch back to text mode anytime</span>
                </div>
              </div>
              <button className="w-full py-3 bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-400 hover:to-cyan-400 text-white font-semibold rounded-lg transition-all transform group-hover:scale-105">
                Enter Visual Preview
              </button>
            </div>
          </div>
        )}

        </div>

        {/* Features Comparison */}
        <div className="bg-slate-800/40 rounded-lg border border-slate-700 p-6">
          <h3 className="text-lg font-semibold text-slate-200 mb-4 text-center">Mode Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-cyan-400 mb-2">Solo Journey Features:</h4>
              <ul className="space-y-1 text-sm text-slate-300">
                <li>• Personal story progression</li>
                <li>• Save & resume anytime</li>
                <li>• AI companions (JAX & Lyra)</li>
                <li>• Voice interface support</li>
                <li>• Custom difficulty scaling</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-fuchsia-400 mb-2">Shared Reality Features:</h4>
              <ul className="space-y-1 text-sm text-slate-300">
                <li>• Real-time collaborative story</li>
                <li>• Player companions + AI guides</li>
                <li>• Shared world consequences</li>
                <li>• Room sharing & invites</li>
                <li>• Asynchronous choice system</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModeSelectionScreen; 
